import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Building2, Inbox, BarChart3, FolderOpen, Users, Calendar, FileSignature, LineChart, DollarSign, Landmark, ListChecks } from "lucide-react";
import { toast } from "sonner";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRole";
import ResourcesTab from "@/components/partner-hub/ResourcesTab";
import TeamTab from "@/components/partner-hub/TeamTab";
import EventsTab from "@/components/partner-hub/EventsTab";
import MouTab from "@/components/partner-hub/MouTab";
import OutcomesTab from "@/components/partner-hub/OutcomesTab";
import PayoutsTab from "@/components/partner-hub/PayoutsTab";
import AnchorPageTab from "@/components/partner-hub/AnchorPageTab";
import OnboardingChecklistTab from "@/components/partner-hub/OnboardingChecklistTab";

type Affiliate = {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  description: string | null;
  website: string | null;
  contact_email: string | null;
  location: string | null;
  category: string;
  services: string[];
  status: string;
  tier: string;
  show_contact_email_public: boolean;
};

type Lead = {
  id: string;
  affiliate_id: string;
  name: string;
  email: string;
  organization: string | null;
  project_summary: string;
  budget_range: string | null;
  timeline: string | null;
  status: string;
  notes: string | null;
  created_at: string;
};

const LEAD_STATUSES = ["new", "contacted", "closed"] as const;

const PartnerHub = () => {
  const { user, loading: authLoading } = useAuth();
  const { loading: rolesLoading, partnerOrgIds, isPartnerAdmin } = useRoles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!activeOrgId && partnerOrgIds.length > 0) setActiveOrgId(partnerOrgIds[0]);
  }, [partnerOrgIds, activeOrgId]);

  const orgsQuery = useQuery({
    enabled: partnerOrgIds.length > 0,
    queryKey: ["partner-orgs", partnerOrgIds.join(",")],
    queryFn: async (): Promise<Affiliate[]> => {
      const { data, error } = await supabase
        .from("affiliates")
        .select(
          "id, slug, name, tagline, description, logo_url, website, location, category, subcategories, services, specialties, status, tier, featured, owner_user_id, show_contact_email_public, created_at, updated_at"
        )
        .in("id", partnerOrgIds);
      if (error) throw error;
      // contact_email is hidden at the DB level; partner admins fetch it
      // on demand via the get_my_affiliate_contact_email RPC.
      return ((data ?? []) as any[]).map((r) => ({ ...r, contact_email: null })) as Affiliate[];
    },
  });

  const activeOrg = useMemo(
    () => orgsQuery.data?.find((o) => o.id === activeOrgId) ?? null,
    [orgsQuery.data, activeOrgId],
  );

  if (authLoading || rolesLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="pt-40 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!isPartnerAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="pt-40 container mx-auto px-6 max-w-2xl text-center space-y-4">
          <h1 className="font-display text-3xl">Partner Hub</h1>
          <p className="text-muted-foreground">
            This area is for approved partner organizations. If your organization should have access,
            apply through our affiliate program and our team will get you set up.
          </p>
          <Button asChild>
            <a href="/affiliates/apply">Apply to partner</a>
          </Button>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="pt-32 pb-20 container mx-auto px-6 max-w-6xl">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Partner Hub</p>
            <h1 className="font-display text-3xl md:text-4xl mt-1">
              {activeOrg?.name ?? "Your organization"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your listing, work inbound leads, and see how your focus areas are moving across Alabama.
            </p>
          </div>
          {partnerOrgIds.length > 1 && (
            <Select value={activeOrgId ?? ""} onValueChange={(v) => setActiveOrgId(v)}>
              <SelectTrigger className="w-[260px]"><SelectValue placeholder="Select org" /></SelectTrigger>
              <SelectContent>
                {(orgsQuery.data ?? []).map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </header>

        {!activeOrg ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <Tabs defaultValue="checklist" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-6 lg:grid-cols-11">
              <TabsTrigger value="checklist"><ListChecks className="h-4 w-4 mr-2" />Checklist</TabsTrigger>
              <TabsTrigger value="profile"><Building2 className="h-4 w-4 mr-2" />Org Profile</TabsTrigger>
              <TabsTrigger value="mou"><FileSignature className="h-4 w-4 mr-2" />Agreement</TabsTrigger>
              <TabsTrigger value="leads"><Inbox className="h-4 w-4 mr-2" />Leads</TabsTrigger>
              <TabsTrigger value="events"><Calendar className="h-4 w-4 mr-2" />Events</TabsTrigger>
              <TabsTrigger value="outcomes"><LineChart className="h-4 w-4 mr-2" />Outcomes</TabsTrigger>
              <TabsTrigger value="payouts"><DollarSign className="h-4 w-4 mr-2" />Payouts</TabsTrigger>
              <TabsTrigger value="anchor"><Landmark className="h-4 w-4 mr-2" />Anchor Page</TabsTrigger>
              <TabsTrigger value="insights" disabled><BarChart3 className="h-4 w-4 mr-2" />Insights</TabsTrigger>
              <TabsTrigger value="resources"><FolderOpen className="h-4 w-4 mr-2" />Resources</TabsTrigger>
              <TabsTrigger value="team"><Users className="h-4 w-4 mr-2" />Team</TabsTrigger>
            </TabsList>

            <TabsContent value="checklist" className="mt-6">
              <OnboardingChecklistTab orgId={activeOrg.id} orgSlug={activeOrg.slug} orgTier={activeOrg.tier} />
            </TabsContent>

            <TabsContent value="profile" className="mt-6">
              <OrgProfileForm org={activeOrg} onSaved={() => queryClient.invalidateQueries({ queryKey: ["partner-orgs"] })} />
            </TabsContent>

            <TabsContent value="mou" className="mt-6">
              <MouTab orgId={activeOrg.id} orgName={activeOrg.name} />
            </TabsContent>

            <TabsContent value="leads" className="mt-6">
              <LeadsBoard orgId={activeOrg.id} />
            </TabsContent>

            <TabsContent value="events" className="mt-6">
              <EventsTab orgId={activeOrg.id} />
            </TabsContent>

            <TabsContent value="outcomes" className="mt-6">
              <OutcomesTab orgId={activeOrg.id} />
            </TabsContent>

            <TabsContent value="payouts" className="mt-6">
              <PayoutsTab orgId={activeOrg.id} />
            </TabsContent>

            <TabsContent value="anchor" className="mt-6">
              <AnchorPageTab orgId={activeOrg.id} orgSlug={activeOrg.slug} orgTier={activeOrg.tier} />
            </TabsContent>

            <TabsContent value="insights" className="mt-6">
              <Card className="p-6 text-sm text-muted-foreground">Coming in phase 2 — anonymized rollups of ideas, grant interest, and legislation activity in your focus areas.</Card>
            </TabsContent>
            <TabsContent value="resources" className="mt-6">
              <ResourcesTab orgId={activeOrg.id} />
            </TabsContent>
            <TabsContent value="team" className="mt-6">
              <TeamTab orgId={activeOrg.id} orgName={activeOrg.name} />
            </TabsContent>
          </Tabs>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

function OrgProfileForm({ org, onSaved }: { org: Affiliate; onSaved: () => void }) {
  const [form, setForm] = useState<Affiliate>(org);
  useEffect(() => {
    setForm(org);
    // Contact email is column-restricted; load it via secure RPC for partner admins.
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc(
        "get_my_affiliate_contact_email" as any,
        { _affiliate_id: org.id },
      );
      if (!cancelled) setForm((f) => ({ ...f, contact_email: (data as string | null) ?? null }));
    })();
    return () => {
      cancelled = true;
    };
  }, [org.id]);

  const mutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("affiliates")
        .update({
          name: form.name,
          tagline: form.tagline,
          description: form.description,
          website: form.website,
          contact_email: form.contact_email,
          location: form.location,
          show_contact_email_public: form.show_contact_email_public,
        })
        .eq("id", org.id);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Profile updated"); onSaved(); },
    onError: (e: any) => toast.error(e.message ?? "Failed to update"),
  });

  return (
    <Card className="p-6 space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Organization name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <Label>Location</Label>
          <Input value={form.location ?? ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Mobile, AL" />
        </div>
        <div className="md:col-span-2">
          <Label>Tagline</Label>
          <Input value={form.tagline ?? ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} maxLength={140} />
        </div>
        <div className="md:col-span-2">
          <Label>Description</Label>
          <Textarea rows={5} value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div>
          <Label>Website</Label>
          <Input value={form.website ?? ""} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" />
        </div>
        <div>
          <Label>Contact email</Label>
          <Input type="email" value={form.contact_email ?? ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.show_contact_email_public}
          onChange={(e) => setForm({ ...form, show_contact_email_public: e.target.checked })}
        />
        Show contact email on the public profile
      </label>

      <div className="flex items-center justify-between border-t border-border pt-4">
        <div className="text-xs text-muted-foreground">
          Status: <Badge variant="outline" className="ml-1">{org.status}</Badge>
        </div>
        <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save changes
        </Button>
      </div>
    </Card>
  );
}

function LeadsBoard({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const leadsQuery = useQuery({
    queryKey: ["partner-leads", orgId],
    queryFn: async (): Promise<Lead[]> => {
      const { data, error } = await supabase
        .from("affiliate_leads")
        .select("*")
        .eq("affiliate_id", orgId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Lead[];
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Lead> }) => {
      const { error } = await supabase.from("affiliate_leads").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["partner-leads", orgId] }),
    onError: (e: any) => toast.error(e.message ?? "Failed to update"),
  });

  if (leadsQuery.isLoading) {
    return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const leads = leadsQuery.data ?? [];
  const grouped = LEAD_STATUSES.map((s) => ({ status: s, items: leads.filter((l) => l.status === s) }));

  if (leads.length === 0) {
    return <Card className="p-8 text-center text-sm text-muted-foreground">No leads yet. Inquiries submitted through your public profile will appear here.</Card>;
  }

  return (
    <div className="grid md:grid-cols-3 gap-4">
      {grouped.map((col) => (
        <div key={col.status}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-sm uppercase tracking-wider text-muted-foreground">{col.status}</h3>
            <Badge variant="outline">{col.items.length}</Badge>
          </div>
          <div className="space-y-3">
            {col.items.map((lead) => (
              <Card key={lead.id} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-medium text-sm">{lead.name}</div>
                    <a href={`mailto:${lead.email}`} className="text-xs text-muted-foreground hover:text-foreground">{lead.email}</a>
                    {lead.organization && <div className="text-xs text-muted-foreground">{lead.organization}</div>}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-3">{lead.project_summary}</p>
                {(lead.budget_range || lead.timeline) && (
                  <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {lead.budget_range && <Badge variant="secondary">{lead.budget_range}</Badge>}
                    {lead.timeline && <Badge variant="secondary">{lead.timeline}</Badge>}
                  </div>
                )}
                <Textarea
                  rows={2}
                  placeholder="Internal notes…"
                  defaultValue={lead.notes ?? ""}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== (lead.notes ?? "")) update.mutate({ id: lead.id, patch: { notes: v } });
                  }}
                  className="text-xs"
                />
                <Select
                  value={lead.status}
                  onValueChange={(v) => update.mutate({ id: lead.id, patch: { status: v } })}
                >
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default PartnerHub;